import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { endBreak, startBreak } from "@/config/mutation/employee";
import { formatDuration } from "@/components/break/format";

const BREAK_TODAY_KEY = ["employee", "break-today"];
const START_KEY = ["break", "start"];
const END_KEY = ["break", "end"];

function errorText(error: unknown): string {
  if (error instanceof AxiosError) {
    return (error.response?.data?.error as string | undefined) || error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong";
}

/** Start/end mutations with toasts; both refresh the polled status query. */
export function useBreakActions() {
  const key = Cookies.get("key") as string;
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: BREAK_TODAY_KEY });

  const start = useMutation({
    mutationKey: START_KEY,
    mutationFn: startBreak(key),
    onSuccess: () => {
      toast.success("Break started. Press Resume when you are back.");
      refresh();
    },
    onError: (error) => {
      toast.error(errorText(error));
      refresh();
    },
  });

  const end = useMutation({
    mutationKey: END_KEY,
    mutationFn: endBreak(key),
    onSuccess: (data) => {
      toast.success(`Welcome back! Break lasted ${formatDuration(data.break.durationSeconds)}.`);
      refresh();
    },
    onError: (error) => {
      toast.error(errorText(error));
      refresh();
    },
  });

  // Counted across every instance of this hook (navbar and banner), so both
  // Resume buttons disable together while one request is in flight.
  const isStarting = useIsMutating({ mutationKey: START_KEY }) > 0;
  const isEnding = useIsMutating({ mutationKey: END_KEY }) > 0;

  return {
    startBreak: start.mutate,
    endBreak: end.mutate,
    isStarting,
    isEnding,
  };
}
